export interface FacetFilterFieldConfig {
  /** UI filter column key, e.g. 'entity_name' */
  key: string;
  /** Danish label shown in the filter dropdown */
  label: string;
  /** Backend SQL column (optional — audit logs use JSON filters instead) */
  sqlColumn?: string;
  /** Optional enum translation for facet values */
  labelMap?: Record<string, string>;
}

export interface FacetFilterConfig {
  fields: FacetFilterFieldConfig[];
}

export interface FacetOption {
  value: string;
  label: string;
  count: number;
}

export interface FacetFilterTableFilter {
  column: string;
  value: string | null;
  operator: 'equals';
  label: string;
  options: Array<{ value: string; label: string; count?: number }>;
}

/** Subset used when preserving the user's current facet selection on refresh. */
export type FacetFilterExistingSelection = Pick<FacetFilterTableFilter, 'column' | 'value'>;

/**
 * Maps server-side facet counts to dynamic-table filter dropdowns.
 * Preserves currently selected values when options refresh.
 */
export function mapFacetsToTableFilters(
  facets: Record<string, FacetOption[]> | undefined | null,
  facetConfig: FacetFilterConfig,
  existingFilters?: FacetFilterExistingSelection[]
): FacetFilterTableFilter[] {
  if (!facetConfig?.fields?.length) {
    return [];
  }

  const existingByColumn = new Map(
    (existingFilters ?? []).map((f) => [f.column, f])
  );

  return facetConfig.fields.map((field) => {
    const facetOptions = facets?.[field.key] ?? [];
    const existing = existingByColumn.get(field.key);

    const options = facetOptions.map((opt) => {
      const baseLabel =
        field.labelMap?.[opt.value] ??
        field.labelMap?.[opt.value.toLowerCase()] ??
        opt.label;
      return {
        value: opt.value,
        label: `${baseLabel} (${opt.count})`,
        count: opt.count,
      };
    });

    return {
      column: field.key,
      value:
        existing?.value != null && existing.value !== ''
          ? String(existing.value)
          : null,
      operator: 'equals' as const,
      label: field.label,
      options,
    };
  });
}
